import type { DataGridCellRendererProps } from '@myosh/odin-components';
import { useMemo } from 'react';
import type { EntityReference } from '../../@types/views';
import type { LinkedRecordsMap } from '../../hooks/use-fetch-linked-record-column-values';
import { getColumnValuePath } from '../../hooks/use-grid-columns-settings';
import { extractFieldName } from '../../utils/data-grid-column-utils';
import { forceAssert } from '../../utils/type-guards';
import RecordlinkBaseDataCell from './recordlink-base-data-cell';
import { getRecordlinkRenderer } from './recordlink-renderer/renderer-registry';

interface RowDataWithPrefetchedRecords {
  _linkedRecordColumnValuesMap?: LinkedRecordsMap;
  fields?: Record<string, unknown>;
  id?: number | string;
}

/** Renders reverse linked records using prefetched data */
const PrefetchedReverseRecordlinkDataCell = ({
  column,
  rowData,
  linkedRecordColumnValuesMap,
}: DataGridCellRendererProps & { linkedRecordColumnValuesMap: LinkedRecordsMap }) => {
  const columnEntityReference = forceAssert<EntityReference>(column.customDataProperties);
  const { entityId, entityPropertyName, linkedFieldId, linkedEntityType, linkedFieldType } = columnEntityReference;

  const linkedColumnEntityReference = {
    entityId: linkedFieldId!,
    entityType: linkedEntityType!,
    entityPropertyName: extractFieldName(entityPropertyName),
  };

  const valuePath = getColumnValuePath(linkedColumnEntityReference);

  const recordIds = useMemo(
    () => forceAssert<Record<string, Array<string>>>(rowData?.fields)?.[entityId] || [],
    [rowData.fields, entityId]
  );

  const records = useMemo(() => {
    return recordIds.map((id) => linkedRecordColumnValuesMap[Number(id)]).filter(Boolean);
  }, [linkedRecordColumnValuesMap, recordIds]);

  // Look up a specialized renderer; fall back to the generic base cell
  const SpecializedRenderer = getRecordlinkRenderer(linkedFieldType);
  if (SpecializedRenderer) {
    return (
      <SpecializedRenderer
        records={records}
        valuePath={valuePath}
        columnWidth={column.width}
        columnEntityReference={columnEntityReference}
        linkedFieldType={linkedFieldType}
      />
    );
  }

  return <RecordlinkBaseDataCell records={records} valuePath={valuePath} width={column.width} type={linkedFieldType} />;
};

/** A custom grid data cell that resolves the correct value to display when the column is 'REVERSE_RECORD_LINK' type */
const ReverseRecordlinkDataCell = ({ column, rowData }: DataGridCellRendererProps) => {
  const typedRowData = rowData as RowDataWithPrefetchedRecords;
  const linkedRecordColumnValuesMap = typedRowData._linkedRecordColumnValuesMap ?? {};

  return (
    <PrefetchedReverseRecordlinkDataCell
      column={column}
      rowData={rowData}
      linkedRecordColumnValuesMap={linkedRecordColumnValuesMap}
    />
  );
};

export default ReverseRecordlinkDataCell;
